// Listen before POST: the server's socket update can beat the HTTP response.
// This confirms authoritative player state, not completion of cosmetic downloads.
export function awaitAvatarSelection(avatarType: string, timeoutMs = 30000) {
  let finish: (accepted: boolean) => void;
  const promise = new Promise<boolean>((resolve) => {
    const onAccepted = (event: Event) => {
      try {
        const detail = (event as CustomEvent).detail;
        const data = typeof detail === 'string' ? JSON.parse(detail) : detail;
        if (data?.avatarType === avatarType) finish(true);
      } catch { /* Ignore malformed bridge messages. */ }
    };
    const onDisconnect = () => finish(false);
    const timer = window.setTimeout(() => finish(false), timeoutMs);
    finish = (accepted) => {
      window.clearTimeout(timer);
      window.removeEventListener('AvatarSelectionAccepted', onAccepted);
      window.removeEventListener('ClientReconnecting', onDisconnect);
      resolve(accepted);
    };
    window.addEventListener('AvatarSelectionAccepted', onAccepted);
    window.addEventListener('ClientReconnecting', onDisconnect);
  });
  return { cancel: () => finish(false), promise };
}

/** Bound HTTP as well as the acknowledgement. Retry only an already-saved,
 * identical selection whose cold-start socket delivery wasn't acknowledged. */
export async function submitAvatarSelection(
  avatarType: string,
  send: (signal: AbortSignal) => Promise<{ status: number }>,
  requireConfirmation: boolean,
  onSaved?: () => void
) {
  let disconnected = false;
  const onDisconnect = () => { disconnected = true; };
  window.addEventListener('ClientReconnecting', onDisconnect);
  try {
    for (let attempt = 0; attempt < 2; attempt++) {
      const confirmation = requireConfirmation ? awaitAvatarSelection(avatarType, 20000) : null;
      const controller = new AbortController();
      let timer: number | undefined;
      try {
        const response = await Promise.race([
          send(controller.signal),
          new Promise<never>((_, reject) => {
            timer = window.setTimeout(() => {
              controller.abort();
              reject(new Error('The avatar service is taking too long. Please try your selection again.'));
            }, 15000);
          }),
        ]);
        window.clearTimeout(timer);
        if (response.status !== 200 && response.status !== 204)
          throw new Error('Could not save your avatar. Please try again.');
        // The player is already ready. A missing socket acknowledgement must not
        // keep the full-screen picker (and its movement lock) open for 20 seconds.
        // Continue verifying/retrying below, but report persistence separately.
        if (attempt === 0) onSaved?.();
        if (!confirmation || await confirmation.promise) return;
        if (disconnected) throw new Error('Reconnecting to the hub. Please choose your avatar once connected.');
      } finally {
        window.clearTimeout(timer);
        controller.abort();
        confirmation?.cancel();
      }
    }
    throw new Error('Your avatar was saved, but the game has not confirmed it yet. Please try your selection again.');
  } finally { window.removeEventListener('ClientReconnecting', onDisconnect); }
}
